import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetAttachments } from "api/queries/shipments/useGetAttachments";
import Button from "components/v2/Button/Button";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { classNames, formatFileSize } from "helpers/commonHelpers";
import showErrorToast from "helpers/errorToast";

import { DownloadAttachmentsSpinner } from "../components/DownloadAttachmentsSpinner";

import {
  downloadAllShipmentAttachmentsWithErrorHandling,
  type ShipmentAttachment,
} from "../attachmentsApi";
import { DetailsSheetSection } from "../DetailsSheetSection";
import { SHIPMENT_DETAILS_TEST_IDS, shipmentDetailsTestId } from "../testIds";

type AttachmentsSectionProps = {
  shipmentId: string;
  isQuickRequest?: boolean;
};

export function AttachmentsSection({ shipmentId, isQuickRequest = false }: AttachmentsSectionProps) {
  const intl = useIntl();
  const fallbackBucketName = intl.formatMessage({
    id: "new-request.attachments.attachment.attachment",
    defaultMessage: "Attachment",
  });
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const isDownloadingAllRef = useRef(false);
  const { data: attachments = [], isInitialLoading, error } = useGetAttachments(shipmentId);

  useEffect(() => {
    if (error) showErrorToast(error);
  }, [error]);

  const groupedAttachments = useMemo(() => {
    const groups: Record<string, ShipmentAttachment[]> = {};
    attachments.forEach((attachment) => {
      const bucket = attachment.bucket_name || fallbackBucketName;
      if (!groups[bucket]) groups[bucket] = [];
      groups[bucket].push(attachment);
    });
    return groups;
  }, [attachments, fallbackBucketName]);

  const handleDownloadAllAttachments = () => {
    if (isDownloadingAllRef.current) {
      return;
    }
    isDownloadingAllRef.current = true;
    setIsDownloadingAll(true);
    downloadAllShipmentAttachmentsWithErrorHandling(
      shipmentId,
      () => {
        isDownloadingAllRef.current = false;
        setIsDownloadingAll(false);
      },
      isQuickRequest,
    );
  };

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_ATTACHMENTS}
      title={<FormattedMessage id="quick-request-detail.attachments" defaultMessage="Attachments" />}
      headerAction={
        !isInitialLoading && attachments.length > 0 ? (
          <Button
            variant="secondary-gray"
            size="md"
            className={classNames("shipment-details-sheet__download-all-attachments", "hide-during-print")}
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_DOWNLOAD_ALL_ATTACHMENTS}
            onClick={handleDownloadAllAttachments}
            disabled={isDownloadingAll}
            iconLeft={isDownloadingAll ? <DownloadAttachmentsSpinner /> : undefined}
            label={
              <FormattedMessage
                id="tracking.downloadAllAttachments"
                defaultMessage="Download all attachments"
              />
            }
          />
        ) : undefined
      }
    >
      <div className="rfv2-review__section-content hide-during-print">
        {isInitialLoading ? (
          <FilePreview isLoading fileName="" />
        ) : attachments.length === 0 ? (
          <div
            className="text-center text-secondary my-2"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ATTACHMENTS_EMPTY}
          >
            <FormattedMessage
              id="quote-request-detail.noAttachmentsFound"
              defaultMessage="No attachments found"
            />
          </div>
        ) : (
          <div className="rfv2-review__docs-grid" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ATTACHMENTS_LIST}>
            {Object.entries(groupedAttachments).map(([bucketName, bucketAttachments]) =>
              bucketAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="rfv2-review__doc-cell"
                  data-testid={shipmentDetailsTestId.attachment(attachment.id)}
                >
                  <span className="rfv2-review__doc-bucket-name">{bucketName}</span>
                  <FilePreview
                    fileName={attachment.name}
                    url={attachment.url}
                    fileSize={attachment.size == null ? undefined : formatFileSize(attachment.size)}
                  />
                </div>
              )),
            )}
          </div>
        )}
      </div>
    </DetailsSheetSection>
  );
}
